import { NavLink } from 'react-router'
import {
  LayoutDashboard,
  Mic,
  X,
  Users,
  Phone,
  History,
  Megaphone,
  FlaskConical,
  Building2,
  KeyRound,
  BarChart3,
  ShieldCheck,
  Sparkles,
  LogOut,
  ListTodo,
  BookUser,
  Bot,
  ChevronDown,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useMe } from '../../api/auth'
import { LanguageSwitcher } from './LanguageSwitcher'

function avatarInitials(name: string, email: string): string {
  const source = (name || email || '').trim()
  if (!source) return '?'
  const parts = source.split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

interface NavItem {
  to: string
  icon: typeof LayoutDashboard
  labelKey: string
  group?: string
  end?: boolean
}

// Voysi flamingo nav. Dashboard lives at /app, every other authed page is
// `/app/<slug>` — the moved-routes change in App.tsx is mirrored here.
const navItems: NavItem[] = [
  { to: '/app', icon: LayoutDashboard, labelKey: 'nav.dashboard', end: true },
  { to: '/app/contacts', icon: Users, labelKey: 'nav.contacts' },
  { to: '/app/follow-ups', icon: ListTodo, labelKey: 'nav.followUps' },
  { to: '/app/calls/stage', icon: Phone, labelKey: 'nav.call' },
  { to: '/app/calls', icon: History, labelKey: 'nav.callHistory', end: true },
  { to: '/app/campaigns', icon: Megaphone, labelKey: 'nav.campaigns' },
  { to: '/app/voice-test', icon: Mic, labelKey: 'nav.voiceTest' },
  { to: '/app/agents', icon: Bot, labelKey: 'nav.agents' },
  { to: '/app/organizations', icon: Building2, labelKey: 'nav.organizations', group: 'org' },
  { to: '/app/members', icon: Users, labelKey: 'nav.members', group: 'org' },
  { to: '/app/directory', icon: BookUser, labelKey: 'nav.directory', group: 'org' },
  { to: '/app/tokens', icon: KeyRound, labelKey: 'nav.apiTokens', group: 'org' },
  { to: '/app/usage', icon: BarChart3, labelKey: 'nav.usage', group: 'org' },
]

const superadminItems: { to: string; icon: typeof LayoutDashboard; labelKey: string }[] = [
  { to: '/app/admin', icon: ShieldCheck, labelKey: 'nav.billingAndPricing' },
  { to: '/app/simulations', icon: FlaskConical, labelKey: 'nav.simulations' },
  { to: '/app/pilot-leads', icon: Sparkles, labelKey: 'nav.pilotLeads' },
]

interface SidebarProps {
  open: boolean
  onClose: () => void
}

// Visual reference: voysi-shell.jsx::VoysiSidebar.
// Background #0E0D11 (ink-900), 220px fixed width, flamingo active state.
// Active rows get a rgba flamingo bg + a tiny flamingo dot on the right.
export function Sidebar({ open, onClose }: SidebarProps) {
  const { t } = useTranslation('common')
  const { data: me } = useMe()
  const isSuperadmin = me?.is_superadmin === true
  const logoutAction = '/logout'

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    [
      'group relative flex items-center gap-3 rounded-[10px] px-3 py-2.5 text-[13px] transition-colors',
      isActive
        ? 'bg-[rgba(255,61,132,0.16)] font-semibold text-white'
        : 'font-medium text-[#A8A6A1] hover:bg-white/5 hover:text-white',
    ].join(' ')

  const iconClass = (isActive: boolean) =>
    `h-4 w-4 ${isActive ? 'text-flamingo-500' : 'text-[#6E6C67]'}`

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-[220px] flex-col gap-0.5 border-r border-[#1F1D23] bg-[#0E0D11] px-3 py-4 text-white transition-transform duration-200 md:static md:translate-x-0 ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Brand lockup */}
        <div className="flex items-center justify-between px-2.5 pb-4 pt-1">
          <div className="flex items-center gap-2.5">
            <img src="/brand/logo-mark.svg" width={28} height={28} alt="Voysi" />
            <span className="font-display text-[17px] font-extrabold tracking-tight">
              Voysi<span className="text-flamingo-500">AI</span>
            </span>
          </div>
          <button
            onClick={onClose}
            aria-label={t('header.closeMenu')}
            className="rounded-md p-1 text-[#6E6C67] hover:bg-white/5 hover:text-white md:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto">
          {navItems.filter((i) => !i.group).map(({ to, icon: Icon, labelKey, end }) => (
            <NavLink key={to} to={to} end={end} onClick={onClose} className={navLinkClass}>
              {({ isActive }) => (
                <>
                  <Icon className={iconClass(isActive)} />
                  <span className="flex-1">{t(labelKey)}</span>
                  {isActive && (
                    <span className="absolute right-2 h-1.5 w-1.5 rounded-full bg-flamingo-500" />
                  )}
                </>
              )}
            </NavLink>
          ))}

          <div className="px-3 pb-1 pt-4 text-[10px] font-bold uppercase tracking-[0.08em] text-[#6E6C67]">
            {t('navGroups.organization')}
          </div>
          {navItems.filter((i) => i.group === 'org').map(({ to, icon: Icon, labelKey }) => (
            <NavLink key={to} to={to} onClick={onClose} className={navLinkClass}>
              {({ isActive }) => (
                <>
                  <Icon className={iconClass(isActive)} />
                  <span className="flex-1">{t(labelKey)}</span>
                  {isActive && (
                    <span className="absolute right-2 h-1.5 w-1.5 rounded-full bg-flamingo-500" />
                  )}
                </>
              )}
            </NavLink>
          ))}

          {isSuperadmin && (
            <>
              <div className="px-3 pb-1 pt-4 text-[10px] font-bold uppercase tracking-[0.08em] text-[#6E6C67]">
                {t('navGroups.superadmin')}
              </div>
              {superadminItems.map(({ to, icon: Icon, labelKey }) => (
                <NavLink key={to} to={to} onClick={onClose} className={navLinkClass}>
                  {({ isActive }) => (
                    <>
                      <Icon className={iconClass(isActive)} />
                      <span className="flex-1">{t(labelKey)}</span>
                      {isActive && (
                        <span className="absolute right-2 h-1.5 w-1.5 rounded-full bg-flamingo-500" />
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        {me?.user && (
          <div className="mt-2 flex flex-col gap-2">
            <NavLink
              to="/app/profile"
              onClick={onClose}
              className="flex items-center gap-2.5 rounded-[14px] border border-[#27242B] bg-[#1A181D] p-3 transition-colors hover:border-[#3A363F]"
            >
              {me.user.avatar_url ? (
                <img
                  src={me.user.avatar_url}
                  alt=""
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : (
                <div
                  className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white"
                  style={{ background: 'linear-gradient(135deg,#FF3D84,#FF7FAC)' }}
                >
                  {avatarInitials(me.user.display_name, me.user.email)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-[12px] font-semibold text-white">
                  {me.user.display_name || me.user.email}
                </div>
                {me.memberships[0]?.org?.name && (
                  <div className="truncate text-[10px] text-[#9E9C96]">
                    {me.memberships[0].org.name}
                  </div>
                )}
              </div>
              <ChevronDown className="h-3.5 w-3.5 text-[#6E6C67]" />
            </NavLink>

            <form method="post" action={logoutAction}>
              <button
                type="submit"
                className="flex w-full items-center gap-3 rounded-[10px] px-3 py-2 text-[13px] font-medium text-[#A8A6A1] transition-colors hover:bg-white/5 hover:text-white"
              >
                <LogOut className="h-4 w-4" />
                {t('nav.logout')}
              </button>
            </form>
          </div>
        )}
        <div className="border-t border-[#1F1D23] pt-2">
          <LanguageSwitcher />
        </div>
      </aside>
    </>
  )
}
